import { formatAdministrativeExpensesRate } from '@/features/mold/utils/standard-fund/private-csv';
import { isBefore2025 } from '@/features/project/_utils/project';
import type {
  FundAdditionalIncomeAndExpenditureModel,
  FundAdditionalIncomeAndExpenditureType,
  PeriodScopeType,
} from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import { t } from '@/i18n';
import {
  BigCalculator,
  convertToNegative,
  handleZeroValueWithNegativeCount,
} from '@/utils';
import type { z } from 'zod';

type ReturnValue = {
  value: string | number | null | undefined;
  isAggregateValueZero: boolean;
};

export const convertPriorToList = ({
  value,
}: {
  value: FundAdditionalIncomeAndExpenditureType;
}): FundAdditionalIncomeAndExpenditureType[] => {
  const list: FundAdditionalIncomeAndExpenditureType[] = [];

  const addPriorToList = (
    incomeAndExpenditureData:
      | FundAdditionalIncomeAndExpenditureType
      | null
      | undefined,
  ) => {
    if (incomeAndExpenditureData) {
      list.push(incomeAndExpenditureData);
      addPriorToList(incomeAndExpenditureData.prior);
    }
  };

  addPriorToList(value.prior);

  return list;
};

/**
 * 帳票プレビュー用：過去年度データを年度降順でソートし、最新N年分を取得する
 * @param value - 基金収支データ
 * @param fiscalYear - 現在の会計年度
 * @param maxYears - 取得する最大年数（デフォルト: 3）
 * @returns 年度付き過去年度データの配列（新しい順）
 */
export const getRecentFundIncomeAndExpenditureList = ({
  value,
  fiscalYear,
  maxYears = 3,
}: {
  value: FundAdditionalIncomeAndExpenditureType;
  fiscalYear: number;
  maxYears?: number;
}): (FundAdditionalIncomeAndExpenditureType & { fiscalYear: number })[] => {
  return convertPriorToList({ value })
    .map((v, i) => ({
      ...v,
      fiscalYear: fiscalYear - i - 1,
    }))
    .sort((a, b) => b.fiscalYear - a.fiscalYear) // 年度降順でソート
    .slice(0, maxYears); // 最新N年分を取得
};

const getSameFieldByYear = (
  yearKeys: number[],
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>,
  fieldName: string,
): Record<string, number> => {
  const result = yearKeys.reduce<Record<string, number>>((acc, year) => {
    acc[year] = fundItemMap[year]?.[fieldName] ?? null;
    return acc;
  }, {});

  return result;
};

export const calcAdministrativeExpensesRate = ({
  administrativeExpense,
  sum,
  hideUnit = false,
}: {
  administrativeExpense: number | null | undefined;
  sum: string | number | bigint | null | undefined;
  hideUnit?: boolean;
}): string | null => {
  // 分子（管理費）がnullまたはundefinedの場合はnullを返す
  if (administrativeExpense === null || administrativeExpense === undefined) {
    return null;
  }

  // 分母がnullまたはundefinedの場合または0の場合はnullを返す
  if (!sum || typeof sum !== 'number' || sum === 0) {
    return null;
  }

  const value = BigCalculator.divide(administrativeExpense, sum);
  return formatAdministrativeExpensesRate(value, hideUnit);
};

const getAdministrativeExpensesRateByYear = (
  yearKeys: number[],
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>,
  isDebugMode: boolean,
): Record<string, string | null> => {
  const result = yearKeys.reduce<Record<string, string | null>>((acc, year) => {
    const currentFundItem = fundItemMap[year];
    if (currentFundItem) {
      const administrativeExpense = currentFundItem.administrativeExpense;
      const summary = getExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: currentFundItem,
        type: 'previous-year-performance',
        isDebugMode,
      });

      acc[year] = calcAdministrativeExpensesRate({
        administrativeExpense,
        sum: summary.value,
      });
    }
    return acc;
  }, {});
  return result;
};

export const getTotalValue = ({
  targets,
  isDebugMode,
}: {
  // 再起的にgetTotalValueがあるため、stringには「--」もしくは「打ち消し」が入ってくる可能性がある。
  // そのためstringが来たらbullと同様に扱う
  targets: (number | undefined | null | string)[];
  isDebugMode: boolean;
}): ReturnValue => {
  const numberTargets = targets.flatMap((v) =>
    typeof v === 'number' ? [v] : [],
  );

  if (targets.every((v) => v === undefined || v === null)) {
    // 純粋なnull判定。
    return { value: null, isAggregateValueZero: false };
  }

  // 打ち消しnullと数値0の確認用のロジック。
  const hasAllNullValue = targets.every(
    (v) => v === undefined || v === null || typeof v === 'string',
  );

  const hasNegativeValue = numberTargets.some((v) => v < 0);
  const value = numberTargets.reduce((acc, v) => BigCalculator.add(acc, v), 0);

  const { fallbackValue, isAggregateValueZero } =
    handleZeroValueWithNegativeCount(value, {
      negativeCount:
        (value === 0 && hasAllNullValue) || hasNegativeValue ? 1 : 0,
      isDebugMode,
    });
  return { value: fallbackValue, isAggregateValueZero };
};

export const getIncomeSummaryByType = ({
  fundAdditionalIncomeAndExpenditure,
  type,
  isDebugMode,
}: {
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType;
  type: PeriodScopeType;
  isDebugMode: boolean;
}): ReturnValue => {
  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.grantedAmountByTheCountry,
      fundAdditionalIncomeAndExpenditure.investmentIncome,
      fundAdditionalIncomeAndExpenditure.businessIncome,
      fundAdditionalIncomeAndExpenditure.otherIncomeAmount,
    ];
    return getTotalValue({ targets, isDebugMode });
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.estimatedGrantedAmountByTheCountry,
      fundAdditionalIncomeAndExpenditure.estimatedInvestmentIncome,
      fundAdditionalIncomeAndExpenditure.estimatedBusinessIncome,
      fundAdditionalIncomeAndExpenditure.estimatedOtherIncomeAmount,
    ];
    return getTotalValue({ targets, isDebugMode });
  }
  const targets = [
    fundAdditionalIncomeAndExpenditure.estimatedGrantedAmountByTheCountryNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedInvestmentIncomeNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedBusinessIncomeNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedOtherIncomeAmountNextYear,
  ];
  return getTotalValue({ targets, isDebugMode });
};

export const getExpenditureSummaryByType = ({
  fundAdditionalIncomeAndExpenditure,
  type,
  isDebugMode,
}: {
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType;
  type: PeriodScopeType;
  isDebugMode: boolean;
}): ReturnValue => {
  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.businessExpense,
      fundAdditionalIncomeAndExpenditure.administrativeExpense,
    ];
    return getTotalValue({ targets, isDebugMode });
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.estimatedBusinessExpense,
      fundAdditionalIncomeAndExpenditure.estimatedAdministrativeExpense,
    ];
    return getTotalValue({ targets, isDebugMode });
  }

  const targets = [
    fundAdditionalIncomeAndExpenditure.estimatedBusinessExpenseNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedAdministrativeExpenseNextYear,
  ];
  return getTotalValue({ targets, isDebugMode });
};

export const getIncomeAndExpenditureSummaryByType = ({
  fundAdditionalIncomeAndExpenditure,
  type,
  isDebugMode,
}: {
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType;
  type: PeriodScopeType;
  isDebugMode: boolean;
}): ReturnValue => {
  const { value: incomeValue } = getIncomeSummaryByType({
    fundAdditionalIncomeAndExpenditure,
    type,
    // 生データが欲しいためfalseにする
    isDebugMode: false,
  });

  const { value: expenditureValue } = getExpenditureSummaryByType({
    fundAdditionalIncomeAndExpenditure,
    type,
    // 生データが欲しいためfalseにする
    isDebugMode: false,
  });

  const incomeAndExpenditureSummary = [
    incomeValue,
    typeof expenditureValue === 'number'
      ? convertToNegative(expenditureValue)
      : expenditureValue,
  ];

  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.beginningOfTheFundBalance,
      ...incomeAndExpenditureSummary,
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.amountReturnedToTheNationalTreasury,
      ),
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.amountReturnedToTheOrganization,
      ),
    ];

    return getTotalValue({
      targets,
      isDebugMode,
    });
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.beginningOfTheFundBalance,
      ...incomeAndExpenditureSummary,
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheNationalTreasury,
      ),
      convertToNegative(
        fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheOrganization,
      ),
    ];
    return getTotalValue({
      targets,
      isDebugMode,
    });
  }

  const targets = [
    fundAdditionalIncomeAndExpenditure.beginningOfTheEstimatedFundBalanceNextYear,
    ...incomeAndExpenditureSummary,
    convertToNegative(
      fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheNationalTreasuryNextYear,
    ),
    convertToNegative(
      fundAdditionalIncomeAndExpenditure.estimatedAmountReturnedToTheOrganizationNextYear,
    ),
  ];
  return getTotalValue({
    targets,
    isDebugMode,
  });
};

const getYearToValueRecordOfFunc = ({
  yearKeys,
  fundItemMap,
  type,
  getValueByType,
  isDebugMode,
}: {
  yearKeys: number[];
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>;
  type: PeriodScopeType;
  getValueByType:
    | typeof getIncomeSummaryByType
    | typeof getExpenditureSummaryByType
    | typeof getIncomeAndExpenditureSummaryByType;
  isDebugMode: boolean;
}): Record<string, string | number | bigint | null | undefined> => {
  const result = yearKeys.reduce<
    Record<string, string | number | bigint | null | undefined>
  >((acc, year) => {
    const currentFundItem = fundItemMap[year];
    if (currentFundItem) {
      const { value } = getValueByType({
        fundAdditionalIncomeAndExpenditure: currentFundItem,
        type,
        isDebugMode,
      });
      acc[year] = value;
    }
    return acc;
  }, {});
  return result;
};

const getIncomeAndExpenditureSummariesByYear = ({
  yearKeys,
  fundItemMap,
  type,
  isDebugMode,
}: {
  yearKeys: number[];
  fundItemMap: Record<string, FundAdditionalIncomeAndExpenditureType>;
  type: PeriodScopeType;
  isDebugMode: boolean;
}): Record<string, string | number | null | undefined> => {
  const result = yearKeys.reduce<
    Record<string, string | number | null | undefined>
  >((acc, year) => {
    const fundItem = fundItemMap[year];
    if (!fundItem) {
      return acc;
    }
    acc[year] = getIncomeAndExpenditureSummaryByType({
      fundAdditionalIncomeAndExpenditure: fundItem,
      type,
      isDebugMode,
    }).value;
    return acc;
  }, {});
  return result;
};

export const getClericalExpenditureSummaryByType = ({
  fundAdditionalIncomeAndExpenditure,
  type,
  isDebugMode,
}: {
  fundAdditionalIncomeAndExpenditure: FundAdditionalIncomeAndExpenditureType;
  type: PeriodScopeType;
  isDebugMode: boolean;
}): ReturnValue => {
  if (type === 'previous-year-performance') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.choreExpensesNotFromThisFund,
      fundAdditionalIncomeAndExpenditure.personnelExpensesNotFromThisFund,
    ];
    return getTotalValue({ targets, isDebugMode });
  }
  if (type === 'estimated-this-year') {
    const targets = [
      fundAdditionalIncomeAndExpenditure.estimatedChoreExpensesNotFromThisFund,
      fundAdditionalIncomeAndExpenditure.estimatedPersonnelExpensesNotFromThisFund,
    ];
    return getTotalValue({ targets, isDebugMode });
  }

  const targets = [
    fundAdditionalIncomeAndExpenditure.estimatedChoreExpensesNotFromThisFundNextYear,
    fundAdditionalIncomeAndExpenditure.estimatedPersonnelExpensesNotFromThisFundNextYear,
  ];
  return getTotalValue({ targets, isDebugMode });
};

export type FundIncomeAndExpenditureDataKey =
  | 'fund-balance-at-the-end-of-previous-fiscal-year'
  | 'income'
  | 'investment-income'
  | 'national-equivalent-amount-of-investment-income'
  | 'business-income'
  | 'national-equivalent-amount-of-business-income'
  | 'others-income'
  | 'total-amount'
  | 'expense'
  | 'administrative-expenses'
  | 'administrative-expenses-rate'
  | 'chore-expenses-of-fund-establishing-of-that'
  | 'personnel-expenses-of-fund-establishing-of-that'
  | 'total-epense-amount'
  | 'returned-to-the-national-treasury'
  | 'returned-to-the-organization'
  | 'fund-balance-at-the-end-of-fiscal-year'
  | 'national-equivalent-amount-of-fund-balance'
  | 'clerical-personnel-expenses'
  | 'personnel-expense'
  | 'total-amount-clerical-expenses';

type YearValue = {
  value: number | string | null | undefined;
  isAggregateValueZero: boolean;
};

// TODO: 過去年度のobjectについては型指定できていない。対応するためには生成ロジックの見直しが必要なので、一旦このようにしている。
export type FundIncomeAndExpenditureDataSourceType = {
  currentYear: YearValue;
  nextYear: YearValue;
  key: FundIncomeAndExpenditureDataKey;
  mainHeaderName: string;
  headerName: string | null;
};

export const getFundIncomeAndExpenditureDataSource = ({
  fiscalYear,
  isDebugMode,
  pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear,
  data,
}: {
  fiscalYear: number;
  isDebugMode: boolean;
  data: z.infer<typeof FundAdditionalIncomeAndExpenditureModel>;
  pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear: (FundAdditionalIncomeAndExpenditureType & {
    fiscalYear: number;
  })[];
}): FundIncomeAndExpenditureDataSourceType[] => {
  const pastYearKeys =
    pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear.map(
      (value) => value.fiscalYear,
    );
  const getYearToFundItemMap = () => {
    const yearToFundItemMap =
      pastFundAdditionalIncomeAndExpenditureListSortedByFiscalYear.reduce<
        Record<string, FundAdditionalIncomeAndExpenditureType>
      >((acc, current) => {
        acc[current.fiscalYear] = current;
        return acc;
      }, {});

    return yearToFundItemMap;
  };

  const yearToFundItemMap = getYearToFundItemMap();
  return [
    {
      key: 'fund-balance-at-the-end-of-previous-fiscal-year',
      mainHeaderName: t('fund-balance-at-the-end-of-previous-fiscal-year'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'beginningOfTheFundBalance',
      ),
      currentYear: {
        value: data.beginningOfTheFundBalance,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.beginningOfTheEstimatedFundBalanceNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'income',
      mainHeaderName: t('income'),
      headerName: t('granted-amount-by-the-country'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'grantedAmountByTheCountry',
      ),
      currentYear: {
        value: data.estimatedGrantedAmountByTheCountry,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedGrantedAmountByTheCountryNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'investment-income',
      mainHeaderName: 'hidden',
      headerName: t('investment-income'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'investmentIncome',
      ),
      currentYear: {
        value: data.estimatedInvestmentIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedInvestmentIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'national-equivalent-amount-of-investment-income',
      mainHeaderName: 'hidden',
      headerName: t('national-equivalent-amount-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'nationalEquivalentOfInvestmentIncome',
      ),
      currentYear: {
        value: data.estimatedNationalEquivalentOfInvestmentIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedNationalEquivalentOfInvestmentIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'business-income',
      mainHeaderName: 'hidden',
      headerName: t('business-income'),
      ...getSameFieldByYear(pastYearKeys, yearToFundItemMap, 'businessIncome'),
      currentYear: {
        value: data.estimatedBusinessIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedBusinessIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'national-equivalent-amount-of-business-income',
      mainHeaderName: 'hidden',
      headerName: t('national-equivalent-amount-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'nationalEquivalentOfBusinessIncome',
      ),
      currentYear: {
        value: data.estimatedNationalEquivalentOfBusinessIncome,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedNationalEquivalentOfBusinessIncomeNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'others-income',
      mainHeaderName: 'hidden',
      headerName: t('others'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'otherIncomeAmount',
      ),
      currentYear: {
        value: data.estimatedOtherIncomeAmount,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedOtherIncomeAmountNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'total-amount',
      mainHeaderName: 'hidden',
      headerName: t('total-amount'),
      ...getYearToValueRecordOfFunc({
        yearKeys: pastYearKeys,
        fundItemMap: yearToFundItemMap,
        type: 'previous-year-performance',
        getValueByType: getIncomeSummaryByType,
        isDebugMode,
      }),
      currentYear: getIncomeSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-this-year',
        isDebugMode,
      }),
      nextYear: getIncomeSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-next-year',
        isDebugMode,
      }),
    },
    {
      key: 'expense',
      mainHeaderName: t('expense'),
      headerName: t('business-expenses'),
      ...getSameFieldByYear(pastYearKeys, yearToFundItemMap, 'businessExpense'),
      currentYear: {
        value: data.estimatedBusinessExpense,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedBusinessExpenseNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'administrative-expenses',
      mainHeaderName: 'hidden',
      headerName: t('administrative-expenses'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'administrativeExpense',
      ),
      currentYear: {
        value: data.estimatedAdministrativeExpense,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedAdministrativeExpenseNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'administrative-expenses-rate',
      mainHeaderName: 'hidden',
      headerName: t('administrative-expenses-rate'),
      ...getAdministrativeExpensesRateByYear(
        pastYearKeys,
        yearToFundItemMap,
        isDebugMode,
      ),
      currentYear: {
        value: calcAdministrativeExpensesRate({
          administrativeExpense: data.estimatedAdministrativeExpense,
          sum: getExpenditureSummaryByType({
            fundAdditionalIncomeAndExpenditure: data,
            type: 'estimated-this-year',
            isDebugMode,
          }).value,
        }),
        isAggregateValueZero: false,
      },
      nextYear: {
        value: calcAdministrativeExpensesRate({
          administrativeExpense: data.estimatedAdministrativeExpenseNextYear,
          sum: getExpenditureSummaryByType({
            fundAdditionalIncomeAndExpenditure: data,
            type: 'estimated-next-year',
            isDebugMode,
          }).value,
        }),
        isAggregateValueZero: false,
      },
    },
    {
      key: 'chore-expenses-of-fund-establishing-of-that',
      mainHeaderName: 'hidden',
      headerName: t('chore-expenses-of-fund-establishing-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'choreExpensesOfFundEstablishing',
      ),
      currentYear: {
        value: data.estimatedChoreExpensesOfFundEstablishing,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedChoreExpensesOfFundEstablishingNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'personnel-expenses-of-fund-establishing-of-that',
      mainHeaderName: 'hidden',
      headerName: t('personnel-expenses-of-fund-establishing-of-that'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'personnelExpensesOfFundEstablishing',
      ),
      currentYear: {
        value: data.estimatedPersonnelExpensesOfFundEstablishing,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedPersonnelExpensesOfFundEstablishingNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'total-epense-amount',
      mainHeaderName: 'hidden',
      headerName: t('total-amount'),
      ...getYearToValueRecordOfFunc({
        yearKeys: pastYearKeys,
        fundItemMap: yearToFundItemMap,
        type: 'previous-year-performance',
        getValueByType: getExpenditureSummaryByType,
        isDebugMode,
      }),
      currentYear: getExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-this-year',
        isDebugMode,
      }),
      nextYear: getExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-next-year',
        isDebugMode,
      }),
    },
    {
      key: 'returned-to-the-national-treasury',
      mainHeaderName: t('returned-to-the-national-treasury'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'amountReturnedToTheNationalTreasury',
      ),
      currentYear: {
        value: data.estimatedAmountReturnedToTheNationalTreasury,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedAmountReturnedToTheNationalTreasuryNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'returned-to-the-organization',
      mainHeaderName: isBefore2025({ fiscalYear })
        ? t('returned-to-the-organization-before-2025')
        : t('returned-to-the-organization'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'amountReturnedToTheOrganization',
      ),
      currentYear: {
        value: data.estimatedAmountReturnedToTheOrganization,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedAmountReturnedToTheOrganizationNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      // 当年度末基金残高
      key: 'fund-balance-at-the-end-of-fiscal-year',
      mainHeaderName: t('fund-balance-at-the-end-of-fiscal-year'),
      headerName: null,
      ...getIncomeAndExpenditureSummariesByYear({
        yearKeys: pastYearKeys,
        fundItemMap: yearToFundItemMap,
        type: 'previous-year-performance',
        isDebugMode,
      }),
      currentYear: getIncomeAndExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-this-year',
        isDebugMode,
      }),
      nextYear: getIncomeAndExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-next-year',
        isDebugMode,
      }),
    },
    {
      key: 'national-equivalent-amount-of-fund-balance',
      mainHeaderName: t('national-equivalent-amount-of-that'),
      headerName: null,
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'nationalEquivalentOfTheBalance',
      ),
      currentYear: {
        value: data.estimatedNationalEquivalentOfTheBalance,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedNationalEquivalentOfTheBalanceNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'clerical-personnel-expenses',
      mainHeaderName: t('clerical-personnel-expenses'),
      headerName: t('chore-expense'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'choreExpensesNotFromThisFund',
      ),
      currentYear: {
        value: data.estimatedChoreExpensesNotFromThisFund,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedChoreExpensesNotFromThisFundNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'personnel-expense',
      mainHeaderName: 'hidden',
      headerName: t('personnel-expense'),
      ...getSameFieldByYear(
        pastYearKeys,
        yearToFundItemMap,
        'personnelExpensesNotFromThisFund',
      ),
      currentYear: {
        value: data.estimatedPersonnelExpensesNotFromThisFund,
        isAggregateValueZero: false,
      },
      nextYear: {
        value: data.estimatedPersonnelExpensesNotFromThisFundNextYear,
        isAggregateValueZero: false,
      },
    },
    {
      key: 'total-amount-clerical-expenses',
      mainHeaderName: 'hidden',
      headerName: t('total-amount'),
      ...getYearToValueRecordOfFunc({
        yearKeys: pastYearKeys,
        fundItemMap: yearToFundItemMap,
        type: 'previous-year-performance',
        getValueByType: getClericalExpenditureSummaryByType,
        isDebugMode,
      }),
      currentYear: getClericalExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-this-year',
        isDebugMode,
      }),
      nextYear: getClericalExpenditureSummaryByType({
        fundAdditionalIncomeAndExpenditure: data,
        type: 'estimated-next-year',
        isDebugMode,
      }),
    },
  ];
};
